/**
 * Empresas — confirmar contas empresariais.
 *
 * Confirmar uma empresa não é carimbar um formulário: a partir daí, quem lá
 * está submete obras em nome dela e aparece assim perante os prestadores.
 * Por isso o ecrã põe à frente o que há para verificar — NIF, representante,
 * contactos — e as pendentes vêm primeiro.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Building2, Check, X, Clock, FileText } from 'lucide-react';
import api from '../utils/api';
import { useToast } from '../hooks/useToast';

const ROTULO_DOC = { ALVARA: 'Alvará', CERTIDAO_COMERCIAL: 'Certidão comercial' };

// Os campos que se podem emendar depois do registo. O estado não está aqui:
// muda-se por confirmar ou recusar, que é outra decisão e tem outro registo.
const CAMPOS = [
  ['nome', 'Nome da empresa'],
  ['nif', 'NIF'],
  ['representante', 'Representante'],
  ['cargo', 'Cargo'],
  ['telefone', 'Telefone'],
  ['email', 'Email'],
  ['endereco', 'Endereço'],
];

const ESTADOS = {
  pending:  ['bg-amber-500/15 text-amber-400', 'Por confirmar'],
  active:   ['bg-green-500/15 text-green-400', 'Confirmada'],
  rejected: ['bg-red-500/15 text-red-400', 'Recusada'],
};

const dt = (v) => (v ? new Date(v).toLocaleDateString('pt-PT') : '—');

const BusinessesPage = () => {
  const { success, error } = useToast();
  const [lista, setLista] = useState(null);
  const [decisao, setDecisao] = useState(null);   // { b, estado, motivo }
  const [edicao, setEdicao] = useState(null);     // { b, campos, motivo }
  const [busy, setBusy] = useState(null);

  const carregar = useCallback(() => {
    api.get('/businesses')
      .then((r) => setLista(r.data || []))
      .catch(() => { setLista([]); error('Erro ao carregar as empresas'); });
  }, [error]);
  useEffect(() => { carregar(); }, [carregar]);

  // O motivo é pedido nas duas decisões, e não só na recusa.
  //
  // Na recusa é o que a empresa vai ler — recusar em silêncio deixa alguém
  // sem saber o que corrigir. Na confirmação é o que fica no registo: a
  // partir daí aquela gente submete obras em nome da empresa perante os
  // prestadores, e era a decisão que dava poderes sem deixar rasto de
  // porquê. Ver o NIF e o representante no cartão ao lado é o trabalho;
  // escrever o que se verificou é o que o torna auditável.
  //
  // A pergunta é feita numa caixa desta página e não no `prompt()` do
  // navegador. O `prompt` é bloqueado em navegadores embutidos e em várias
  // configurações, e quando é bloqueado a decisão morre sem erro nenhum: o
  // botão parece avariado e a empresa fica à espera de uma resposta que
  // ninguém sabe que não saiu.
  const decidir = (b, estado) => setDecisao({
    b,
    estado,
    motivo: estado === 'rejected' ? '' : 'NIF e representante conferidos',
  });

  // O ficheiro vai por rota autenticada — abrir o URL directo num separador
  // não passa o cabeçalho e o servidor recusa.
  const abrirDoc = (b, d) => {
    api.get(`/businesses/${b.business_id}/documentos/${d.documento_id}/ficheiro`,
      { responseType: 'blob' })
      .then((r) => window.open(URL.createObjectURL(r.data), '_blank'))
      .catch(() => error('Não foi possível abrir o documento.'));
  };

  const gravarCorreccao = async () => {
    const { b, campos, motivo } = edicao;
    setBusy(b.business_id);
    try {
      const { data } = await api.put(`/businesses/${b.business_id}`,
        { ...campos, motivo: motivo.trim() });
      success(data?.inalterado ? 'Não havia nada para mudar.' : `${data.nome} corrigida.`);
      setEdicao(null);
      carregar();
    } catch (e) { error(e.response?.data?.message || 'Não foi possível corrigir.'); }
    finally { setBusy(null); }
  };

  const confirmarDecisao = async () => {
    const { b, estado, motivo, forcar } = decisao;
    setBusy(b.business_id);
    try {
      const { data } = await api.put(`/businesses/${b.business_id}/status`,
        { estado, motivo: motivo.trim(), forcar: !!forcar });
      success(data?.inalterado
        ? `${b.nome} já estava neste estado.`
        : estado === 'active'
          ? `${b.nome} confirmada. Quem a criou foi avisado.`
          : estado === 'rejected'
            ? `${b.nome} recusada. Quem a criou foi avisado, com o motivo.`
            : `${b.nome} voltou a pendente.`);
      setDecisao(null);
      carregar();
    } catch (e) { error(e.response?.data?.message || 'Não foi possível decidir.'); }
    finally { setBusy(null); }
  };

  const pendentes = (lista || []).filter((b) => b.estado === 'pending').length;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Building2 className="w-6 h-6 text-blue-400" /> Empresas
        </h1>
        <p className="text-sm text-slate-400 mt-1">
          {pendentes > 0
            ? `${pendentes} conta${pendentes > 1 ? 's' : ''} à espera de confirmação.`
            : 'Contas empresariais registadas na plataforma.'}
        </p>
      </div>

      {lista === null ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>
      ) : lista.length === 0 ? (
        <p className="text-slate-500 py-10 text-center">Ainda não há empresas registadas.</p>
      ) : (
        <ul className="space-y-2">
          {lista.map((b) => {
            const [cls, txt] = ESTADOS[b.estado] || ESTADOS.pending;
            return (
              <li key={b.business_id} className="p-4 rounded-xl border border-slate-800 bg-slate-900/50">
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-semibold text-slate-100">{b.nome}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      NIF <span className="font-mono">{b.nif}</span> · {b.obras} obra{b.obras === 1 ? '' : 's'}
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-md text-[11px] font-semibold ${cls}`}>{txt}</span>
                </div>

                <dl className="grid grid-cols-2 gap-x-4 gap-y-1 mt-3 text-xs">
                  {[
                    ['Representante', b.representante + (b.cargo ? ` (${b.cargo})` : '')],
                    ['Telefone', b.telefone || '—'],
                    ['Email', b.email || '—'],
                    ['Endereço', b.endereco || '—'],
                    ['Criada por', b.criador || '—'],
                    ['Registada em', dt(b.created_at)],
                  ].map(([k, v]) => (
                    <div key={k} className="flex gap-2 min-w-0">
                      <dt className="text-slate-500 shrink-0">{k}:</dt>
                      <dd className="text-slate-300 truncate">{v}</dd>
                    </div>
                  ))}
                </dl>

                {/* Os documentos ficam no cartão, ao lado do NIF e do
                    representante, porque é sobre eles que se decide. Estavam
                    entregues e invisíveis: quem confirmava não sabia sequer
                    que existiam. */}
                {(b.documentos?.length > 0 || b.documentos_em_falta?.length > 0) && (
                  <div className="mt-3 pt-3 border-t border-slate-800">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1.5">
                      Documentos entregues
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {(b.documentos || []).map((d) => (
                        <button key={d.documento_id} onClick={() => abrirDoc(b, d)}
                          className="inline-flex items-center gap-1.5 px-3 min-h-[36px] rounded-lg bg-slate-800 text-slate-200 text-xs">
                          <FileText className="w-3.5 h-3.5" />
                          {ROTULO_DOC[d.tipo] || d.tipo}
                          <span className="text-slate-500">
                            {d.origem === 'EMPRESA' ? 'pela empresa' : 'pela Dozy'}
                          </span>
                        </button>
                      ))}
                    </div>
                    {b.documentos_em_falta?.length > 0 && (
                      <p className="text-xs text-amber-400 mt-2">
                        Em falta: {b.documentos_em_falta.join(', ')}.
                      </p>
                    )}
                  </div>
                )}

                {/* Corrigir vive ao lado de confirmar porque é a alternativa
                    a confirmar com o erro. Sem isto, uma gralha no registo
                    só tinha duas saídas: passar à frente, ou recusar e mandar
                    a empresa registar-se outra vez. */}
                <button onClick={() => setEdicao({
                  b, campos: Object.fromEntries(CAMPOS.map(([k]) => [k, b[k] || ''])), motivo: '',
                })}
                  className="mt-3 text-xs text-slate-400 underline">
                  Corrigir dados
                </button>

                {b.estado === 'pending' && (
                  <div className="flex gap-2 mt-3 flex-wrap">
                    <button onClick={() => decidir(b, 'active')} disabled={busy === b.business_id}
                      className="inline-flex items-center gap-1.5 px-4 min-h-[44px] rounded-lg bg-green-600 text-white font-semibold disabled:opacity-50">
                      <Check className="w-4 h-4" /> Confirmar
                    </button>
                    <button onClick={() => decidir(b, 'rejected')} disabled={busy === b.business_id}
                      className="inline-flex items-center gap-1.5 px-4 min-h-[44px] rounded-lg bg-slate-800 text-red-400 font-medium disabled:opacity-50">
                      <X className="w-4 h-4" /> Recusar
                    </button>
                  </div>
                )}
                {b.estado === 'active' && (
                  <button onClick={() => decidir(b, 'pending')} disabled={busy === b.business_id}
                    className="inline-flex items-center gap-1.5 px-3 min-h-[44px] mt-3 rounded-lg bg-slate-800 text-slate-300 text-sm disabled:opacity-50">
                    <Clock className="w-4 h-4" /> Voltar a pendente
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {edicao && (
        <div className="fixed inset-0 z-50 bg-black/70 p-4 overflow-y-auto"
          role="dialog" aria-modal="true" aria-label="Corrigir dados da empresa">
          <div className="mx-auto w-full max-w-lg rounded-2xl border border-slate-700 bg-slate-900 p-5 my-8">
            <h2 className="text-lg font-bold text-white">Corrigir {edicao.b.nome}</h2>
            <p className="mt-1 text-sm text-slate-400">
              Confira contra a certidão comercial antes de emendar. Fica registado o que
              estava, o que passou a estar, e porquê.
            </p>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {CAMPOS.map(([k, rotulo]) => (
                <label key={k} className={k === 'endereco' || k === 'nome' ? 'sm:col-span-2' : ''}>
                  <span className="block text-xs text-slate-500 mb-1">{rotulo}</span>
                  <input value={edicao.campos[k]}
                    onChange={(e) => setEdicao((d) => ({ ...d, campos: { ...d.campos, [k]: e.target.value } }))}
                    className="w-full px-3 min-h-[44px] rounded-lg bg-slate-950 border border-slate-700 text-slate-100 text-sm" />
                </label>
              ))}
            </div>

            <textarea rows={2} value={edicao.motivo}
              onChange={(e) => setEdicao((d) => ({ ...d, motivo: e.target.value }))}
              placeholder="Ex.: apelido do representante trocado no registo; certidão diz Dias."
              className="mt-3 w-full rounded-lg border border-slate-700 bg-slate-950 p-3 text-sm text-slate-100" />

            <div className="mt-4 flex gap-2 justify-end">
              <button type="button" onClick={() => setEdicao(null)} disabled={busy !== null}
                className="px-4 min-h-[44px] rounded-lg bg-slate-800 text-slate-300 disabled:opacity-50">
                Cancelar
              </button>
              <button type="button" onClick={gravarCorreccao}
                disabled={busy !== null || edicao.motivo.trim().length < 5}
                className="px-4 min-h-[44px] rounded-lg bg-blue-600 text-white font-semibold disabled:opacity-50">
                {busy !== null ? 'A guardar…' : 'Gravar correcção'}
              </button>
            </div>
          </div>
        </div>
      )}

      {decisao && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          role="dialog" aria-modal="true" aria-label="Motivo da decisão">
          <div className="w-full max-w-lg rounded-2xl border border-slate-700 bg-slate-900 p-5">
            <h2 className="text-lg font-bold text-white">
              {decisao.estado === 'rejected'
                ? `Recusar ${decisao.b.nome}`
                : decisao.estado === 'active'
                  ? `Confirmar ${decisao.b.nome}`
                  : `Voltar ${decisao.b.nome} a pendente`}
            </h2>
            <p className="mt-1 text-sm text-slate-400">
              {decisao.estado === 'rejected'
                ? 'O motivo é enviado a quem criou a conta — é por ele que fica a saber o que corrigir.'
                : 'Escreva o que verificou. Fica no registo, associado a si e à data.'}
            </p>

            <textarea
              autoFocus
              rows={3}
              value={decisao.motivo}
              onChange={(e) => setDecisao((d) => ({ ...d, motivo: e.target.value }))}
              placeholder={decisao.estado === 'rejected'
                ? 'Ex.: o NIF não corresponde ao nome da empresa no certificado.'
                : 'Ex.: NIF e representante conferidos no certificado da AGT.'}
              className="mt-3 w-full rounded-lg border border-slate-700 bg-slate-950 p-3 text-sm text-slate-100"
            />

            {decisao.motivo.trim().length < 5 && (
              <p className="mt-1 text-xs text-amber-400">
                Escreva o motivo — esta decisão dá ou retira poderes a uma empresa.
              </p>
            )}

            {/* A caixa de confirmar é o último sítio onde ainda dá para
                parar. Se faltam documentos, diz-se aqui e obriga-se a assumir
                a decisão — o servidor recusa na mesma sem o `forcar`. */}
            {decisao.estado === 'active' && decisao.b.documentos_em_falta?.length > 0 && (
              <label className="mt-3 flex items-start gap-2 text-sm text-amber-300">
                <input type="checkbox" className="mt-1" checked={!!decisao.forcar}
                  onChange={(e) => setDecisao((d) => ({ ...d, forcar: e.target.checked }))} />
                <span>
                  Confirmar sem {decisao.b.documentos_em_falta.join(' e ')}
                  <span className="block text-xs text-slate-400">
                    Só marcar se os documentos foram vistos por outra via. O motivo
                    acima passa a ser a única prova de que alguém decidiu.
                  </span>
                </span>
              </label>
            )}

            <div className="mt-4 flex gap-2 justify-end">
              <button type="button" onClick={() => setDecisao(null)} disabled={busy !== null}
                className="px-4 min-h-[44px] rounded-lg bg-slate-800 text-slate-300 disabled:opacity-50">
                Cancelar
              </button>
              <button type="button" onClick={confirmarDecisao}
                disabled={busy !== null || decisao.motivo.trim().length < 5
                  || (decisao.estado === 'active'
                      && decisao.b.documentos_em_falta?.length > 0 && !decisao.forcar)}
                className={`px-4 min-h-[44px] rounded-lg font-semibold text-white disabled:opacity-50 ${
                  decisao.estado === 'rejected' ? 'bg-red-600' : 'bg-green-600'}`}>
                {busy !== null ? 'A guardar…' : 'Confirmar decisão'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessesPage;
